import React from 'react'
import { number, string, shape } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import CurrencyFormat from 'react-currency-format'
import { Button } from '@gympass/yoga'

import useInternationalization from '@hooks/useInternationalization'

import Divider from '@components/Divider'
import PlanCardDescription from './Components/PlanCardDescription'

import * as Style from './PlanCard.style'

export default function PlanCard({ plan, theme }) {
  const {
    country,
    formats: { currency },
  } = useInternationalization()

  function openPlans() {
    const plansUrlByCountry = {
      us: 'https://hs.gympass.com/hubfs/membership_us.pdf',
      br:
        'https://hs.gympass.com/hubfs/Commercial%20Offer%20BR/membership_br.pdf',
      uk:
        'https://hs.gympass.com/hubfs/Broker%20UK/UK_Employee_membership_fee_v2%20(1).pdf',
      mx: 'https://hs.gympass.com/hubfs/SMB_Home%20BR/MX_SeePlans.pdf ',
    }

    window.open(plansUrlByCountry[country], '_blank')
  }

  return (
    <Style.PlanCardWrapper>
      <Style.Title>{plan.title}</Style.Title>

      <Style.Price color={theme.price.color} iconColor="theme.price.iconColor">
        <i>{currency.moneySign}</i>
        <CurrencyFormat
          value={plan.price}
          displayType="text"
          thousandSeparator={currency.thousandSeparator}
          decimalSeparator={currency.decimalSeparator}
          decimalScale={2}
          fixedDecimalScale
        />
        <em>
          /
          <FormattedMessage id="smb_acquisition_commons.frequency.monthly" />
        </em>
      </Style.Price>

      <PlanCardDescription theme={theme} plan={plan} />

      <Style.DividerWrapper>
        <Divider />
      </Style.DividerWrapper>

      <Style.OfferList>
        <Style.OfferItem>
          <Style.OfferDescription>
            <FormattedMessage id="smb_acquisition_gym_search_map.plans.offer.personal_trainer.description" />
          </Style.OfferDescription>

          <Style.OfferValue>
            <FormattedMessage
              id="smb_acquisition_gym_search_map.plans.offer.personal_trainer.value"
              values={{ value: plan.personalTrainerSessions }}
            />
          </Style.OfferValue>
        </Style.OfferItem>

        <Style.OfferItem>
          <Style.OfferDescription>
            <FormattedMessage id="smb_acquisition_gym_search_map.plans.offer.gyms.description" />
          </Style.OfferDescription>

          <Style.OfferValue>
            <CurrencyFormat
              value={plan.gymCount}
              displayType="text"
              thousandSeparator={currency.thousandSeparator}
              decimalSeparator={currency.decimalSeparator}
            />
          </Style.OfferValue>
        </Style.OfferItem>

        <Style.OfferItem>
          <Style.OfferDescription>
            <FormattedMessage id="smb_acquisition_gym_search_map.plans.offer.apps.description" />
          </Style.OfferDescription>
          <Style.OfferValue>{plan.wellnessApps}</Style.OfferValue>
        </Style.OfferItem>
      </Style.OfferList>

      {theme.buttonOutline && (
        <Style.CardFooter>
          <Style.ButtonOutline customTheme={theme.buttonOutline} full>
            <Style.ButtonText onClick={openPlans}>
              <FormattedMessage id="smb_acquisition_gym_search_map.call_to_action.compare_plans" />
            </Style.ButtonText>
          </Style.ButtonOutline>
        </Style.CardFooter>
      )}

      {!theme.buttonOutline && (
        <Style.CardFooter>
          <Button.Outline full>
            <Style.ButtonText onClick={openPlans}>
              <FormattedMessage id="smb_acquisition_gym_search_map.call_to_action.compare_plans" />
            </Style.ButtonText>
          </Button.Outline>
        </Style.CardFooter>
      )}
    </Style.PlanCardWrapper>
  )
}

PlanCard.propTypes = {
  plan: shape({
    wellnessApps: number,
    gymCount: number,
    therapySession: number,
    personalTrainerSessions: number,
    title: string,
    previousPlan: string,
  }).isRequired,
  theme: shape({
    description: shape({
      color: string,
    }),
    price: shape({
      color: string,
      iconColor: string,
    }),
    buttonOutline: shape({
      borderColor: string,
      border: string,
      color: string,
      focus: shape({
        backgroundColor: string,
      }),
      hover: shape({
        backgroundColor: string,
      }),
      active: shape({
        borderColor: string,
        backgroundColor: string,
        border: string,
        color: string,
      }),
    }),
  }),
}

PlanCard.defaultProps = {
  theme: {
    description: {
      color: '',
    },
    price: {
      color: '',
      iconColor: '',
    },
    buttonOutline: {
      borderColor: '',
      border: '',
      color: '',
      focus: {
        backgroundColor: '',
      },
      hover: {
        backgroundColor: '',
      },
      active: {
        borderColor: '',
        backgroundColor: '',
        border: '',
        color: '',
      },
    },
  },
}
